import { createTheme, alpha } from '@mui/material/styles';

const defaultTheme = createTheme();

const customShadows = [...defaultTheme.shadows];

export const brand = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)',
};

export const gray = {
  50: 'hsl(220, 35%, 97%)',
  100: 'hsl(220, 30%, 94%)',
  200: 'hsl(220, 20%, 88%)',
  300: 'hsl(220, 20%, 80%)',
  400: 'hsl(220, 20%, 65%)',
  500: 'hsl(220, 20%, 42%)',
  600: 'hsl(220, 20%, 35%)',
  700: 'hsl(220, 20%, 25%)',
  800: 'hsl(220, 30%, 6%)',
  900: 'hsl(220, 35%, 3%)',
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const getDesignTokens = (mode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px';

  return {
    palette: {
      mode,
      primary: mode === 'light'
        ? { light: '#4e4848', main: '#212121', dark: '#212121', contrastText: '#f5f4f2' }
        : { light: '#f5f4f2', main: '#f5f4f2', dark: '#b7b4aa', contrastText: '#212121' },
      secondary: mode === 'light'
        ? { light: '#b7b4aa', main: '#b7b4aa', dark: '#4e4848', contrastText: '#212121' }
        : { light: '#4e4848', main: '#4e4848', dark: '#212121', contrastText: '#f5f4f2' },
      info: mode === 'light'
        ? { light: '#A8C9CC', main: '#6FA2A8', dark: '#3E6F78', contrastText: '#212121' }
        : { light: '#7FB5BA', main: '#4C8C93', dark: '#2E5F67', contrastText: '#f5f4f2' },
      warning: mode === 'light'
        ? { light: '#EFE9C9', main: '#D9CC80', dark: '#C7B35D', contrastText: '#212121' }
        : { light: '#E6DCA6', main: '#CDB663', dark: '#AC9850', contrastText: '#212121' },
      error: mode === 'light'
        ? { light: '#F2B184', main: '#E68A4C', dark: '#C8642A', contrastText: '#f5f4f2' }
        : { light: '#E9A36F', main: '#D07A3F', dark: '#B25D26', contrastText: '#f5f4f2' },
      success: mode === 'light'
        ? { light: '#BFD4C8', main: '#9FBDB1', dark: '#4A7E84', contrastText: '#212121' }
        : { light: '#A9C9BC', main: '#6F9E93', dark: '#45716B', contrastText: '#f5f4f2' },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? '#dedcd7' : '#dedcd7',
      background: {
        default: mode === 'light' ? '#f5f4f2' : '#5c0c0cff',
        paper: mode === 'light' ? '#f5f4f2' : '#767676',
      },
      text: {
        primary: mode === 'light' ? '#212121' : '#4b453aff',
        secondary: mode === 'light' ? '#767676' : '#b7b4aa',
        warning: orange[400],
      },
      action: {
        hover: mode === 'light' ? alpha('#767676', 0.08) : alpha(gray[600], 0.2),
        selected: mode === 'light' ? alpha('#767676', 0.12) : alpha(gray[600], 0.3),
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: { light: '#4e4848', main: '#212121', dark: '#212121', contrastText: '#f5f4f2' },
      secondary: { light: '#b7b4aa', main: '#b7b4aa', dark: '#4e4848', contrastText: '#212121' },
      info: { light: '#A8C9CC', main: '#6FA2A8', dark: '#3E6F78', contrastText: '#212121' },
      warning: { light: '#EFE9C9', main: '#D9CC80', dark: '#C7B35D', contrastText: '#212121' },
      error: { light: '#F2B184', main: '#E68A4C', dark: '#C8642A', contrastText: '#f5f4f2' },
      success: { light: '#BFD4C8', main: '#9FBDB1', dark: '#4A7E84', contrastText: '#212121' },
      grey: {
        ...gray,
      },
      divider: '#dedcd7',
      background: {
        default: '#f5f4f2',
        paper: '#f5f4f2',
      },
      text: {
        primary: '#212121',
        secondary: '#767676',
        warning: orange[400],
      },
      action: {
        hover: alpha('#767676', 0.08),
        selected: alpha('#767676', 0.12),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
    },
  },
  dark: {
    palette: {
      primary: { light: '#f5f4f2', main: '#f5f4f2', dark: '#b7b4aa', contrastText: '#212121' },
      secondary: { light: '#4e4848', main: '#4e4848', dark: '#212121', contrastText: '#f5f4f2' },
      info: { light: '#A8C9CC', main: '#6FA2A8', dark: '#3E6F78', contrastText: '#212121' },
      warning: { light: '#EFE9C9', main: '#D9CC80', dark: '#C7B35D', contrastText: '#212121' },
      error: { light: '#F2B184', main: '#E68A4C', dark: '#C8642A', contrastText: '#f5f4f2' },
      success: { light: '#BFD4C8', main: '#9FBDB1', dark: '#4A7E84', contrastText: '#212121' },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: 'hsl(220, 30%, 5%)',
      },
      text: {
        primary: '#f5f4f2',
        secondary: '#b7b4aa',
      },
      action: {
        hover: alpha('#f5f4f2', 0.08),
        selected: alpha('#f5f4f2', 0.12),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

const defaultShadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];

export const shadows = defaultShadows;
